// ABOUTME: Categorized picker for the melding `type` field — 4-column popover (shippers / webshops / API & printnode / ERPs).
// ABOUTME: Each option renders as a colored MeldingTypeBadge + label so categories are visually distinct.

import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

import { tokens } from "../../../../theme";
import PopoverModal from "../../../theme/Modal/PopoverModal";
import Text from "../../../theme/text/Text";
import MeldingTypeBadge from "./MeldingTypeBadge";
import { MELDING_TYPE_CATEGORIES, findMeldingType } from "./meldingTypes";
import { useTypeColors } from "./useTypeColors";

const OptionRow = ({ item, isSelected, onSelect, theme }) => {
    const mode = theme.palette.mode;
    const { bg, color } = useTypeColors(item.id, mode);

    return (
        <ButtonBase
            onClick={() => onSelect(item.id)}
            sx={{
                width: 1,
                px: 1,
                py: 0.75,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                textAlign: "left",
                background: isSelected ? bg : "transparent",
                color: color,
                "&:hover": { background: bg },
            }}
        >
            <MeldingTypeBadge typeId={item.id} size={26} />
            <Box
                sx={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: "Roboto, sans-serif",
                    color: color,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {item.label}
            </Box>
        </ButtonBase>
    );
};

const Column = ({ cat, value, onSelect, colors, theme, t }) => (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
            sx={{
                px: 1,
                py: 1,
                fontSize: 11,
                fontWeight: 700,
                color: colors.txt["secondary"],
                textTransform: "uppercase",
                letterSpacing: 0.5,
                position: "sticky",
                top: 0,
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                zIndex: 1,
            }}
        >
            {t(cat.label) || cat.label}
        </Box>
        {cat.items.length === 0 ? (
            <Box sx={{ px: 1, py: 1 }}>
                <Text light fontSize={11}>{t("no_results") || "Geen"}</Text>
            </Box>
        ) : (
            cat.items.map((item) => (
                <OptionRow
                    key={item.id}
                    item={item}
                    isSelected={value === item.id}
                    onSelect={onSelect}
                    colors={colors}
                    theme={theme}
                />
            ))
        )}
    </Box>
);

const MeldingTypeDropdown = ({ value, onChange }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [search, setSearch] = useState("");

    const selected = findMeldingType(value);

    const filteredCategories = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return MELDING_TYPE_CATEGORIES;
        return MELDING_TYPE_CATEGORIES.map((cat) => ({
            ...cat,
            items: cat.items.filter(
                (item) =>
                    item.label.toLowerCase().includes(q) || item.id.toLowerCase().includes(q)
            ),
        }));
    }, [search]);

    const handleOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearch("");
    };

    const handleSelect = (id) => {
        onChange?.(id);
        handleClose();
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange?.(null);
    };

    return (
        <>
            <ButtonBase
                onClick={handleOpen}
                sx={{
                    width: 1,
                    minHeight: 40,
                    px: 1,
                    py: 0.5,
                    borderRadius: 5,
                    backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: colors.txt["primary"],
                }}
            >
                {selected ? (
                    <>
                        <MeldingTypeBadge typeId={selected.id} size={28} />
                        <Box sx={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <Text fontSize={13}>{selected.label}</Text>
                        </Box>
                        <Box
                            component="span"
                            onClick={handleClear}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                color: colors.txt["secondary"],
                                "&:hover": { background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200] },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ flex: 1, textAlign: "left", pl: 0.5 }}>
                        <Text light fontSize={13}>{t("choose") || "Kies"}</Text>
                    </Box>
                )}
                <ExpandMoreIcon sx={{ fontSize: 18, color: colors.txt["secondary"] }} />
            </ButtonBase>

            <PopoverModal
                id="melding-type-dropdown"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                zIndex={1300}
                triangle={{
                    position: "center",
                    color: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                }}
            >
                <Box
                    sx={{
                        width: 880,
                        maxWidth: "92vw",
                        height: 480,
                        display: "flex",
                        flexDirection: "column",
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                        borderRadius: 3,
                        overflow: "hidden",
                        p: 2,
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            height: 40,
                            borderRadius: 5,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 18, color: colors.txt["secondary"] }} />
                        <InputBase
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("search") || "Zoeken"}
                            sx={{ flex: 1, fontSize: 13, color: colors.txt["primary"] }}
                            autoFocus
                        />
                    </Box>

                    <Box sx={{ flex: 1, display: "flex", overflowY: "auto", overflowX: "hidden", gap: 2, alignItems: "flex-start" }}>
                        {filteredCategories.map((cat) => (
                            <Box
                                key={cat.id}
                                sx={{ flex: 1, minWidth: 0, display: "flex" }}
                            >
                                <Column
                                    cat={cat}
                                    value={value}
                                    onSelect={handleSelect}
                                    colors={colors}
                                    theme={theme}
                                    t={t}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </PopoverModal>
        </>
    );
};

export default MeldingTypeDropdown;
